import type {
  DashboardCardRegistryEntry,
  DashboardCardType,
  DashboardLayout,
  DashboardLayoutItem,
} from "../../../../types/dashboardLayout";
import { DASHBOARD_LAYOUT_VERSION } from "../../../../types/dashboardLayout";

export const DASHBOARD_DEFAULT_LAYOUT_ID = "default";

/** Maximum widgets on the dashboard top (mini stat) row. */
export const DASHBOARD_MAX_MINI_STAT_SLOTS = 4;

export const dashboardCardRegistry: Record<DashboardCardType, DashboardCardRegistryEntry> = {
  portfolio: {
    cardType: "portfolio",
    title: "Portfolio Size",
    removable: true,
    compatibleZones: ["miniStats"],
    defaultItem: { cardType: "portfolio", visible: false, zone: "miniStats", order: 0, x: 0, y: 0, w: 3, h: 2 },
  },
  expectancy: {
    cardType: "expectancy",
    title: "Expectancy",
    removable: true,
    compatibleZones: ["miniStats"],
    defaultItem: { cardType: "expectancy", visible: false, zone: "miniStats", order: 2, x: 6, y: 0, w: 3, h: 2 },
  },
  winRate: {
    cardType: "winRate",
    title: "Win Rate",
    removable: true,
    compatibleZones: ["miniStats"],
    defaultItem: { cardType: "winRate", visible: false, zone: "miniStats", order: 1, x: 3, y: 0, w: 3, h: 2 },
  },
  monthlyPnL: {
    cardType: "monthlyPnL",
    title: "Total P&L",
    removable: true,
    compatibleZones: ["miniStats"],
    defaultItem: { cardType: "monthlyPnL", visible: false, zone: "miniStats", order: 0, x: 0, y: 0, w: 3, h: 2 },
  },
  roi: {
    cardType: "roi",
    title: "Max Drawdown / ROI",
    removable: true,
    compatibleZones: ["miniStats"],
    defaultItem: { cardType: "roi", visible: false, zone: "miniStats", order: 3, x: 9, y: 0, w: 3, h: 2 },
  },
  closedTrades: {
    cardType: "closedTrades",
    title: "Closed Trades",
    removable: true,
    compatibleZones: ["miniStats"],
    defaultItem: { cardType: "closedTrades", visible: false, zone: "miniStats", order: 10, x: 0, y: 0, w: 3, h: 2 },
  },
  activeTrades: {
    cardType: "activeTrades",
    title: "Active Trades",
    removable: true,
    compatibleZones: ["miniStats"],
    defaultItem: { cardType: "activeTrades", visible: false, zone: "miniStats", order: 11, x: 0, y: 0, w: 3, h: 2 },
  },
  largestWin: {
    cardType: "largestWin",
    title: "Largest Win",
    removable: true,
    compatibleZones: ["miniStats"],
    defaultItem: { cardType: "largestWin", visible: false, zone: "miniStats", order: 12, x: 0, y: 0, w: 3, h: 2 },
  },
  averageWin: {
    cardType: "averageWin",
    title: "Average Win",
    removable: true,
    compatibleZones: ["miniStats"],
    defaultItem: { cardType: "averageWin", visible: false, zone: "miniStats", order: 13, x: 0, y: 0, w: 3, h: 2 },
  },
  largestLoss: {
    cardType: "largestLoss",
    title: "Largest Loss",
    removable: true,
    compatibleZones: ["miniStats"],
    defaultItem: { cardType: "largestLoss", visible: false, zone: "miniStats", order: 14, x: 0, y: 0, w: 3, h: 2 },
  },
  averageLoss: {
    cardType: "averageLoss",
    title: "Average Loss",
    removable: true,
    compatibleZones: ["miniStats"],
    defaultItem: { cardType: "averageLoss", visible: false, zone: "miniStats", order: 15, x: 0, y: 0, w: 3, h: 2 },
  },
  portfolioGrowth: {
    cardType: "portfolioGrowth",
    title: "Total P&L Trend",
    removable: false,
    compatibleZones: ["splitRow"],
    defaultItem: {
      cardType: "portfolioGrowth",
      visible: true,
      zone: "splitRow",
      slot: "left",
      ratio: 0.5,
      x: 0,
      y: 2,
      w: 6,
      h: 4,
      minW: 4,
      minH: 3,
    },
  },
  winRateTrend: {
    cardType: "winRateTrend",
    title: "Win Rate Trend",
    removable: false,
    compatibleZones: ["splitRow"],
    defaultItem: {
      cardType: "winRateTrend",
      visible: true,
      zone: "splitRow",
      slot: "right",
      ratio: 0.5,
      x: 6,
      y: 2,
      w: 6,
      h: 4,
      minW: 4,
      minH: 3,
    },
  },
  pnlMilestones: {
    cardType: "pnlMilestones",
    title: "Milestones",
    removable: true,
    compatibleZones: ["splitRow"],
    defaultItem: {
      cardType: "pnlMilestones",
      visible: false,
      zone: "splitRow",
      slot: "left",
      ratio: 0.5,
      x: 0,
      y: 2,
      w: 6,
      h: 4,
      minW: 4,
      minH: 3,
    },
  },
  tradeActivityCalendar: {
    cardType: "tradeActivityCalendar",
    title: "Trade Activity Calendar",
    removable: false,
    compatibleZones: ["fullWidth"],
    defaultItem: {
      cardType: "tradeActivityCalendar",
      visible: true,
      zone: "fullWidth",
      order: 0,
      x: 0,
      y: 6,
      w: 12,
      h: 4,
      minW: 8,
      minH: 3,
    },
  },
  pnlOverTime: {
    cardType: "pnlOverTime",
    title: "Profit & Loss Over Time",
    removable: true,
    compatibleZones: ["fullWidth"],
    defaultItem: { cardType: "pnlOverTime", visible: false, zone: "fullWidth", order: 1, x: 0, y: 8, w: 12, h: 4, minW: 8, minH: 3 },
  },
  tradeDistribution: {
    cardType: "tradeDistribution",
    title: "Trade Distribution",
    removable: true,
    compatibleZones: ["fullWidth"],
    defaultItem: { cardType: "tradeDistribution", visible: false, zone: "fullWidth", order: 2, x: 0, y: 10, w: 12, h: 4, minW: 8, minH: 3 },
  },
  performanceBreakdown: {
    cardType: "performanceBreakdown",
    title: "Trade Outcomes",
    removable: true,
    compatibleZones: ["fullWidth"],
    defaultItem: { cardType: "performanceBreakdown", visible: false, zone: "fullWidth", order: 5, x: 0, y: 16, w: 12, h: 4, minW: 8, minH: 3 },
  },
  streakAnalysis: {
    cardType: "streakAnalysis",
    title: "Streak Analysis",
    removable: true,
    compatibleZones: ["fullWidth"],
    defaultItem: { cardType: "streakAnalysis", visible: false, zone: "fullWidth", order: 6, x: 0, y: 18, w: 12, h: 4, minW: 8, minH: 3 },
  },
  longVsShortPerformance: {
    cardType: "longVsShortPerformance",
    title: "Long vs Short Performance",
    removable: true,
    compatibleZones: ["fullWidth"],
    defaultItem: { cardType: "longVsShortPerformance", visible: false, zone: "fullWidth", order: 7, x: 0, y: 20, w: 12, h: 4, minW: 8, minH: 3 },
  },
  keyRiskMetrics: {
    cardType: "keyRiskMetrics",
    title: "Key Risk Metrics",
    removable: true,
    compatibleZones: ["fullWidth"],
    defaultItem: { cardType: "keyRiskMetrics", visible: false, zone: "fullWidth", order: 8, x: 0, y: 21, w: 12, h: 4, minW: 8, minH: 3 },
  },
  drawdownOverTime: {
    cardType: "drawdownOverTime",
    title: "Drawdown Over Time",
    removable: true,
    compatibleZones: ["fullWidth"],
    defaultItem: { cardType: "drawdownOverTime", visible: false, zone: "fullWidth", order: 9, x: 0, y: 22, w: 12, h: 4, minW: 8, minH: 3 },
  },
  equityPeakPath: {
    cardType: "equityPeakPath",
    title: "Equity / Peak Path",
    removable: true,
    compatibleZones: ["fullWidth"],
    defaultItem: { cardType: "equityPeakPath", visible: false, zone: "fullWidth", order: 10, x: 0, y: 23, w: 12, h: 4, minW: 8, minH: 3 },
  },
};

export const dashboardCardTypesInDefaultOrder: DashboardCardType[] = [
  "portfolio",
  "expectancy",
  "winRate",
  "monthlyPnL",
  "roi",
  "closedTrades",
  "activeTrades",
  "largestWin",
  "averageWin",
  "largestLoss",
  "averageLoss",
  "portfolioGrowth",
  "winRateTrend",
  "pnlMilestones",
  "tradeActivityCalendar",
  "pnlOverTime",
  "tradeDistribution",
  "performanceBreakdown",
  "streakAnalysis",
  "longVsShortPerformance",
  "keyRiskMetrics",
  "drawdownOverTime",
  "equityPeakPath",
];

/** Account-type presets for the built-in default dashboard layout. */
export type DashboardDefaultLayoutVariant = "sync" | "csv";

const DEFAULT_MINI_STAT_CARD_TYPES: DashboardCardType[] = [
  "monthlyPnL",
  "winRate",
  "expectancy",
  "closedTrades",
];

const DEFAULT_VISIBLE_CARD_TYPES: Record<DashboardDefaultLayoutVariant, DashboardCardType[]> = {
  sync: [
    ...DEFAULT_MINI_STAT_CARD_TYPES,
    "portfolioGrowth",
    "winRateTrend",
    "tradeActivityCalendar",
  ],
  csv: [
    ...DEFAULT_MINI_STAT_CARD_TYPES,
    "portfolioGrowth",
    "winRateTrend",
    "tradeActivityCalendar",
  ],
};

function buildDefaultDashboardLayoutItem(
  cardType: DashboardCardType,
  variant: DashboardDefaultLayoutVariant
): DashboardLayoutItem {
  const base = { ...dashboardCardRegistry[cardType].defaultItem };
  const visible = DEFAULT_VISIBLE_CARD_TYPES[variant].includes(cardType);
  if (!visible) {
    return { ...base, visible: false };
  }
  if (base.zone === "miniStats") {
    const order = DEFAULT_MINI_STAT_CARD_TYPES.indexOf(cardType);
    return {
      ...base,
      visible: true,
      zone: "miniStats",
      order,
      x: order * 3,
      y: 0,
      w: 3,
      h: 2,
    };
  }
  return { ...base, visible: true };
}

/** Top-row mini stat widgets the user can add (same order as the add-widget picker). */
export const dashboardMiniStatWidgetCatalog: { cardType: DashboardCardType; title: string }[] =
  dashboardCardTypesInDefaultOrder
    .filter((cardType) => dashboardCardRegistry[cardType].compatibleZones.includes("miniStats"))
    .map((cardType) => ({ cardType, title: dashboardCardRegistry[cardType].title }));

export function getDefaultDashboardLayoutItems(
  variant: DashboardDefaultLayoutVariant = "sync"
): DashboardLayoutItem[] {
  return dashboardCardTypesInDefaultOrder.map((cardType) =>
    buildDefaultDashboardLayoutItem(cardType, variant)
  );
}

export function getDefaultDashboardLayout(
  variant: DashboardDefaultLayoutVariant = "sync"
): DashboardLayout {
  const nowIso = new Date().toISOString();
  return {
    id: DASHBOARD_DEFAULT_LAYOUT_ID,
    name: "Default",
    isDefault: true,
    isActive: true,
    layoutVersion: DASHBOARD_LAYOUT_VERSION,
    items: getDefaultDashboardLayoutItems(variant),
    createdAt: nowIso,
    updatedAt: nowIso,
  };
}
